import { materialScopeDisclosure } from '../../../lib/supplyChainMaterialScope';
import { MaterialScopeLimitationsEditor } from './MaterialScopeLimitationsEditor';
/**
 * SupplyChainPanel — SCM4.
 *
 * 5-tab surface (Equipment (BOM) / Suppliers / Quotes / Timeline / CAPEX) that
 * replaces the "Coming Soon" placeholder under the Equipment (BOM) top-level
 * tab on SiteLandingPage. Mirrors the tab pattern from
 * SiteConfigurationPanel (SC2-3) — local sub-tab state, each sub-panel
 * is its own file so future work can extend them without touching the
 * shell.
 *
 * Data flow:
 *   - Reads `site.supplyChainPlan` (SupplyChainPlan, optional). When the
 *     plan has no user-authored BoM/quotes, renders an upload-first landing
 *     card (PDF/Excel BoM, quote upload, or manual line item).
 *   - Reads `site.financialAssumptions?.inServiceDate` to drive the
 *     critical-path analysis (SCM5).
 *   - Does NOT compute supply-chain risk derivations or itemized CAPEX —
 *     those live with SCM-Integration.
 *
 * Visual notes:
 *   - Tab strip styled identically to the parent SiteLandingPage tab
 *     strip (small caps, 9px/10px font sizes, accent underline on active).
 *   - Body min-height keeps the layout from collapsing when an inner
 *     panel renders the empty-state.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import type { SiteEntity } from '../../../types/site';
import type { ComplianceCertificationId } from '../../../types/supplyChain';
import { SupplyChainOverviewPanel } from './SupplyChainOverviewPanel';
import { SupplyChainBomPanel, type BomAttachmentField } from './SupplyChainBomPanel';
import { DocViewerDialog } from '../DocViewerDialog';
import { SupplyChainSuppliersPanel } from './SupplyChainSuppliersPanel';
import { SupplyChainQuotesPanel } from './SupplyChainQuotesPanel';
import { SupplyChainTimelinePanel } from './SupplyChainTimelinePanel';
import { SupplyChainLandingCard } from './SupplyChainLandingCard';
import { BomUploadReviewDialog } from './BomUploadReviewDialog';
import { BomItemEditDialog, type PhaseChoice } from './BomItemEditDialog';
import { useBomUploadFlow } from './useBomUploadFlow';
import { useQuoteUploadFlow } from './useQuoteUploadFlow';
import { useStore } from '../../../store';
import { openActiveCanvasFromSite } from '../../../store/simulationSiteLaunch';
import { siteLaunchLinkNotice } from '../../../lib/sim/canvas/siteLaunchLink';
import {
  isSupplyChainPlanUserReady,
  SUPPLY_CHAIN_MIN_REPORTABLE_BOM_ITEMS,
} from '../../../lib/supplyChainPlanReady';
import { pruneUnlinkedProjectSuppliers, shouldPruneProjectSuppliers } from '../../../lib/pruneUnlinkedProjectSuppliers';
import { SupplierOutreachDialog } from './SupplierOutreachDialog';
import type { BomItem, Quote, Supplier, SupplyChainPlan } from '../../../types/supplyChain';
import type { BomSourceDocumentChange } from '../../../lib/bomSourceDocumentLink';
import { defaultBomItemForSupplier, supplierOutreachProjectFromSite } from '../../../lib/supplierOutreachHelpers';
import { resolveSupplierDirectory } from '../../../lib/resolveSupplierDirectory';
import { effectivePhases } from '../../../lib/projectIntent';
import { saveCompanySupplier } from '../../../services/supplyChain/curatedSupplierStore';
import { auth } from '../../../firebase';
const GenerateSimulationWizard = React.lazy(() =>
  import('../GenerateSimulationWizard').then((m) => ({ default: m.GenerateSimulationWizard }))
);
import { useNavigate } from 'react-router-dom';
import { isEoEngineActive } from '../../../lib/sim/eo/eoEngineFlag';
import { isBomSimulationGenerationEnabled } from '../../../lib/runtimeFlags';
import {
  Package,
  Users,
  FileSpreadsheet,
  Calendar,
  BarChart3,
  type LucideIcon,
} from 'lucide-react';
import { ResponsiveTabStrip } from '../../ui/responsive-tab-strip';
import { TabShell } from '../../ui/aurora';
import { Dialog, DialogContent, DialogTitle } from '../../ui/dialog';

type SubTab = 'overview' | 'bom' | 'suppliers' | 'quotes' | 'timeline';

const SUB_TABS: { id: SubTab; label: string; shortLabel: string; icon: LucideIcon }[] = [
  { id: 'bom', label: 'Equipment (BOM)', shortLabel: 'BOM', icon: Package },
  { id: 'suppliers', label: 'Suppliers', shortLabel: 'Suppliers', icon: Users },
  { id: 'quotes', label: 'Quotes', shortLabel: 'Quotes', icon: FileSpreadsheet },
  { id: 'timeline', label: 'Timeline', shortLabel: 'Timeline', icon: Calendar },
  { id: 'overview', label: 'CAPEX', shortLabel: 'CAPEX', icon: BarChart3 },
];

export interface SupplyChainPanelProps {
  site: SiteEntity;
  /** Optional default tab — used by tests to land directly on a sub-panel. */
  initialTab?: SubTab;
}

export const SupplyChainPanel: React.FC<SupplyChainPanelProps> = ({ site, initialTab }) => {
  const simulationComputeEnabled = isEoEngineActive();
  const bomSimulationGenerationEnabled = isBomSimulationGenerationEnabled();
  const [activeTab, setActiveTab] = useState<SubTab>(initialTab ?? 'bom');
  const companyId = useStore(s => s.companyId);
  const applyBomFromUpload = useStore(s => s.applyBomFromUpload);
  const addSupplierFromDirectory = useStore(s => s.addSupplierFromDirectory);
  const addSiteDocument = useStore(s => s.addSiteDocument);
  const updateSupplyChainPlan = useStore(s => s.updateSupplyChainPlan);
  const linkBomSourceDocument = useStore(s => s.linkBomSourceDocument);
  const updateBomItemStatus = useStore(s => s.updateBomItemStatus);
  const awardOrRejectQuote = useStore(s => s.awardOrRejectQuote);
  const pullFinancialFromSources = useStore(s => s.pullFinancialFromSources);
  const syncSiteWorksBomFromLayout = useStore(s => s.syncSiteWorksBomFromLayout);
  const [manualAddOpen, setManualAddOpen] = useState(false);
  // BoM→EO canvas generation. Non-ready templates open with explicit repair blockers.
  const [generateSimulationOpen, setGenerateSimulationOpen] = useState(false);
  const setViewMode = useStore(s => s.setViewMode);
  const navigate = useNavigate();
  const [outreachTarget, setOutreachTarget] = useState<{ supplier: Supplier; bomItem: BomItem } | null>(null);
  const attachmentRef = useRef<HTMLInputElement | null>(null);
  const [attachmentTarget, setAttachmentTarget] = useState<
    { type: 'bom'; itemId: string }
    | { type: 'award'; itemId: string }
    | { type: 'quote'; quoteId: string }
    | null
  >(null);
  const [attachmentPickerOpen, setAttachmentPickerOpen] = useState(false);
  const [attachmentDocumentId, setAttachmentDocumentId] = useState('');
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const [linkingExisting, setLinkingExisting] = useState(false);
  const sourceQuoteRetry = useRef<{ draftKey: string; quoteId: string } | null>(null);
  const attachedDocuments = (site.documents ?? []).filter(doc => (
    doc.id && (site.documents ?? []).filter(candidate => candidate.id === doc.id).length === 1
  ));
  /** Upload-in-progress feedback for BoM attachments. Set before the
   *  attachment upload starts, cleared after success OR error so the BoM
   *  row can render an "Uploading…" indicator only while genuinely pending. */
  const [attachBusy, setAttachBusy] = useState<{ itemId: string; field: BomAttachmentField } | null>(null);
  /** Document id whose viewer (DocViewerDialog) is open, opened from the
   *  per-item linked-docs list. */
  const [viewerDocId, setViewerDocId] = useState<string | null>(null);
  const viewerDoc = viewerDocId
    ? (site.documents ?? []).find((d) => d.id === viewerDocId) ?? null
    : null;
  /** SCM13 cross-tab navigation: BoM rows hand the Suppliers tab a filter
   *  set when the user clicks "N suppliers match". Stored as a freshness
   *  counter alongside the filter so re-clicking the same chip still resets
   *  the Suppliers panel (React's `useEffect` keys on referential equality). */
  const [suppliersFilter, setSuppliersFilter] = useState<readonly ComplianceCertificationId[]>([]);
  const [suppliersBomItemId, setSuppliersBomItemId] = useState<string | undefined>();
  const jumpToSuppliersWithFilter = useCallback(
    (filter: readonly ComplianceCertificationId[], bomItemId?: string) => {
      // Always hand a fresh array so the child's effect re-fires.
      setSuppliersFilter([...filter]);
      setSuppliersBomItemId(bomItemId);
      setActiveTab('suppliers');
    },
    [],
  );
  const plan = site.supplyChainPlan;
  const planReady = isSupplyChainPlanUserReady(plan);
  // Editing an incomplete user plan must not depend on report readiness.
  // Template-only plans still start with the existing intake landing.
  const hasUserBom = plan?.bom.some(item => (
    item.derivedFrom === 'manual' || item.derivedFrom === 'uploaded-bom'
  )) ?? false;

  // Supplier-DB link (Add BOM Item): resolve the FULL supplier directory —
  // baseline + the platform-curated Datacrate suppliers + company suppliers —
  // so the "Use supplier catalog" picker in the Add/Edit BOM Item dialog lists
  // the curated DA / admin suppliers, not just this site's plan-local ones.
  // (The Suppliers tab already resolves the directory; the BOM dialog did not.)
  const [directorySuppliers, setDirectorySuppliers] = useState<Supplier[]>([]);
  useEffect(() => {
    let cancelled = false;
    void resolveSupplierDirectory(companyId).then((entries) => {
      if (!cancelled) setDirectorySuppliers(entries);
    });
    return () => { cancelled = true; };
  }, [companyId]);
  // Merge the directory with this site's plan-local suppliers (plan edits win on
  // id collision) — this is what the Add/Edit BOM Item dialog selects from.
  const bomSuppliers = useMemo<Supplier[]>(() => {
    const byId = new Map<string, Supplier>();
    for (const s of directorySuppliers) byId.set(s.id, s);
    for (const s of plan?.suppliers ?? []) byId.set(s.id, s);
    return [...byId.values()];
  }, [directorySuppliers, plan?.suppliers]);

  // A3 — resolve the project's rollout phases so a manually-added BoM line can
  // be assigned to its real phase instead of being silently bucketed into
  // Phase 1 downstream. `effectivePhases` always returns at least one entry
  // (synthetic single-phase for legacy intents); we only expose the picker when
  // the project is genuinely phased — a single synthetic phase carries no
  // meaningful choice and the line's phaseId stays undefined (legacy contract).
  const bomPhases = useMemo<PhaseChoice[]>(() => {
    const view = effectivePhases(site.projectIntent);
    if (!view.isPhased) return [];
    return view.phases.map(p => ({ id: p.id, label: p.label }));
  }, [site.projectIntent]);
  // Seed a new line with the first phase so it lands in Phase 1 explicitly
  // (rather than implicitly), but only when the project is phased.
  const defaultPhaseId = bomPhases[0]?.id;

  // B3 — promote an inline-created site-local supplier to the company directory
  // so it can be reused across sites. Deterministic id mirrors
  // CompanySuppliersPanel (`company-<id>-<slug>`) so re-adding the same name is
  // idempotent. Returns false (and toasts) on any failure so the dialog can
  // surface the error without throwing.
  const handleSaveSupplierToCompany = useCallback(
    async (supplier: Supplier): Promise<boolean> => {
      const cid = companyId ?? site.companyId;
      if (!cid) {
        toast.error('No company context — cannot save to company directory.');
        return false;
      }
      const slug = supplier.name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 40);
      const id = `company-${cid}-${slug}`;
      try {
        await saveCompanySupplier(
          cid,
          {
            id,
            name: supplier.name,
            hq: supplier.hq,
            categories: supplier.categories,
            ...(supplier.certifications ? { certifications: supplier.certifications } : {}),
            ...(supplier.website ? { website: supplier.website } : {}),
          },
          { updatedByEmail: auth.currentUser?.email ?? undefined },
        );
        toast.success(`Saved ${supplier.name} to company directory.`);
        return true;
      } catch (e) {
        toast.error(`Save to company directory failed: ${(e as Error)?.message ?? 'unknown'}`);
        return false;
      }
    },
    [companyId, site.companyId],
  );

  // One-time trim of legacy directory bulk-import pollution on bloated plans.
  useEffect(() => {
    if (!plan || !shouldPruneProjectSuppliers(plan)) return;
    const pruned = pruneUnlinkedProjectSuppliers(plan);
    if (pruned.suppliers.length === plan.suppliers.length) return;
    void updateSupplyChainPlan(site.id, () => pruned);
  }, [site.id, plan, updateSupplyChainPlan]);
  // PMT4 — resolve COD with a clear precedence:
  //   1. FinancialAssumptions.inServiceDate (FA core; authoritative when set)
  //   2. ProjectIntent.inServiceDate (new in PMT2 — surfaces the user's
  //      exact COD even when the FA tab hasn't been filled yet)
  // Falling through to (2) means the SCM critical-path math becomes
  // precise as soon as the user enters a date in the simpler Project
  // Intent surface — they don't have to also fill the FA core.
  const inServiceDate =
    site.financialAssumptions?.inServiceDate ?? site.projectIntent?.inServiceDate ?? null;

  const nowIso = () => new Date().toISOString();
  const generateId = (prefix: string) => `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

  const bomUpload = useBomUploadFlow({
    siteId: site.id,
    defaultMode: hasUserBom ? 'merge' : 'create',
  });

  const quoteUpload = useQuoteUploadFlow({
    siteId: site.id,
    plan,
    onBomCandidate: (candidate) => {
      bomUpload.setExtractedFromExternal(candidate.lines, candidate.documentId, candidate.warnings);
      bomUpload.setMode(hasUserBom ? 'merge' : 'create');
    },
  });

  const handleCreateBomItem = async (payload: Omit<import('../../../types/supplyChain').BomItem, 'id' | 'derivedFrom'>) => {
    const res = await updateSupplyChainPlan(site.id, (current) => ({
      ...current,
      bom: [
        ...current.bom,
        { id: generateId('bom'), ...payload, derivedFrom: 'manual' },
      ],
      updatedAt: nowIso(),
    }));
    if (!res.ok) toast.error(`Add BoM item failed: ${res.error}`);
  };

  const handleUpdateBomItem = async (
    itemId: string,
    payload: Omit<import('../../../types/supplyChain').BomItem, 'id' | 'derivedFrom'>,
  ) => {
    const res = await updateSupplyChainPlan(site.id, (current) => ({
      ...current,
      bom: current.bom.map((item) => (
        item.id === itemId
          ? { ...item, ...payload }
          : item
      )),
      updatedAt: nowIso(),
    }));
    if (!res.ok) toast.error(`Update BoM item failed: ${res.error}`);
  };

  const handleDeleteBomItem = async (item: import('../../../types/supplyChain').BomItem) => {
    if (!window.confirm(`Delete "${item.name}" from the BoM?`)) return;
    const res = await updateSupplyChainPlan(site.id, (current) => ({
      ...current,
      bom: current.bom.filter((b) => b.id !== item.id),
      updatedAt: nowIso(),
    }));
    if (!res.ok) toast.error(`Delete BoM item failed: ${res.error}`);
  };

  const handleAddSupplier = async (supplier: Pick<import('../../../types/supplyChain').Supplier, 'name' | 'hq' | 'categories'>) => {
    const res = await updateSupplyChainPlan(site.id, (current) => ({
      ...current,
      suppliers: [
        ...current.suppliers,
        {
          id: generateId('sup'),
          ...supplier,
        },
      ],
      updatedAt: nowIso(),
    }));
    if (!res.ok) toast.error(`Add supplier failed: ${res.error}`);
  };

  const handleCreateSupplierInline = async (
    supplier: Pick<import('../../../types/supplyChain').Supplier, 'name' | 'hq' | 'categories'>,
  ): Promise<string | null> => {
    const existing = (plan?.suppliers ?? []).find(
      s => s.name.trim().toLowerCase() === supplier.name.trim().toLowerCase(),
    );
    if (existing) return existing.id;
    const id = generateId('sup');
    const res = await updateSupplyChainPlan(site.id, (current) => ({
      ...current,
      suppliers: [...current.suppliers, { id, ...supplier }],
      updatedAt: nowIso(),
    }));
    if (!res.ok) {
      toast.error(`Add supplier failed: ${res.error}`);
      return null;
    }
    return id;
  };

  const handleAttachSupplierToBomItem = async (args: { supplierId: string; bomItemId: string }) => {
    const res = await updateSupplyChainPlan(site.id, (current) => ({
      ...current,
      bom: current.bom.map((item) => {
        if (item.id !== args.bomItemId) return item;
        const next = new Set(item.shortlistedSupplierIds ?? []);
        next.add(args.supplierId);
        return {
          ...item,
          shortlistedSupplierIds: Array.from(next),
        };
      }),
      updatedAt: nowIso(),
    }));
    if (!res.ok) toast.error(`Attach supplier failed: ${res.error}`);
  };

  const handleAddManualQuote = async (draft: import('./SupplyChainQuotesPanel').ManualQuoteDraft) => {
    const draftKey = JSON.stringify({ siteId: site.id, draft });
    const quoteId = draft.documentId && sourceQuoteRetry.current?.draftKey === draftKey
      ? sourceQuoteRetry.current.quoteId
      : generateId('quote');
    if (draft.documentId) sourceQuoteRetry.current = { draftKey, quoteId };
    else sourceQuoteRetry.current = null;
    const addQuote = (current: SupplyChainPlan): SupplyChainPlan => ({
      ...current,
      quotes: [
        ...current.quotes,
        {
          id: quoteId,
          ...draft,
          status: 'open',
        },
      ],
      // Apply quote lead-time/cost immediately so timeline + CAPEX surfaces react.
      bom: current.bom.map((item) => {
        if (item.id !== draft.bomItemId) return item;
        return {
          ...item,
          status: item.status === 'planned' ? 'quoted' : item.status,
          unitCostUsd: draft.unitCostUsd,
          totalCostUsd: item.quantity * draft.unitCostUsd,
          priceSource: 'quote',
          leadTimeSource: 'quote',
          leadTimeMonthsLow: Math.max(0, draft.leadTimeMonths - 1),
          leadTimeMonthsHigh: draft.leadTimeMonths,
          shortlistedSupplierIds: Array.from(new Set([...(item.shortlistedSupplierIds ?? []), draft.supplierId])),
          priceProvenance: {
            ...(item.priceProvenance ?? {}),
            quoteId,
          },
        };
      }),
      updatedAt: nowIso(),
    });
    if (draft.documentId) {
      const { currentPlan } = readAttachedSource(draft.documentId);
      if (currentPlan.bom.filter(item => item.id === draft.bomItemId).length !== 1
        || currentPlan.suppliers.filter(supplier => supplier.id === draft.supplierId).length !== 1) {
        throw new Error('The selected BoM item or supplier has changed. Reopen the quote form.');
      }
      const { documentId, ...quoteDraft } = draft;
      const change: BomSourceDocumentChange = { type: 'add-quote', quoteId, draft: quoteDraft };
      // The authenticated action atomically links this existing source and adds
      // the quote. Keep its ID on an ambiguous failure so retry cannot add twice.
      await linkBomSourceDocument(site.id, documentId, change);
      sourceQuoteRetry.current = null;
    } else {
      const res = await updateSupplyChainPlan(site.id, addQuote);
      if (!res.ok) {
        toast.error(`Add quote failed: ${res.error}`);
        return;
      }
    }
    toast.success('Quote added and applied to BoM lead/cost.');
  };

  const handleLandingManualCreate = async (
    payload: Omit<import('../../../types/supplyChain').BomItem, 'id' | 'derivedFrom'>,
  ) => {
    const item = {
      id: generateId('bom'),
      ...payload,
      derivedFrom: 'manual' as const,
    };
    const res = await applyBomFromUpload({
      siteId: site.id,
      items: [item],
      // The maintained merge action initializes a missing plan and preserves
      // existing rows if another manual save has already reached the store.
      mode: 'merge',
    });
    if (res.ok) {
      toast.success('BoM item added.');
      setManualAddOpen(false);
    } else {
      toast.error(`Add BoM item failed: ${res.error}`);
    }
  };

  const handleUploadQuoteFile = async (file: File) => {
    await quoteUpload.handleFile(file);
  };

  const handleAddQuoteFromBom = (_bomItemId: string) => {
    quoteUpload.openFilePicker();
  };

  const openSupplierEmail = (supplier: Supplier, bomItem: BomItem) => {
    setOutreachTarget({ supplier, bomItem });
  };

  const handleEmailSupplier = (supplier: Supplier, bomItemId?: string) => {
    const bomItem = bomItemId
      ? plan?.bom.find(b => b.id === bomItemId)
      : defaultBomItemForSupplier(plan, supplier);
    if (!bomItem) {
      toast.error('Add a BoM line before emailing suppliers.');
      return;
    }
    openSupplierEmail(supplier, bomItem);
  };

  /*
   * F1 / decision Q5 — ONE SYNC, AND IT IS THE PER-PHASE PULL.
   *
   * Both Sync buttons used to call the CAMPUS sync, including the one sitting
   * on the PER-PHASE CAPEX rollup strip. On any multi-phase model that write
   * cannot be attributed to a phase, so `phases[]` — which is authoritative —
   * was left untouched: the per-phase cells the button sat above stayed empty,
   * the campus figure was reverted by the next save, and the toast reported
   * the dollar amount as though it had landed.
   *
   * `pullFinancialFromSources` is the phase-aware route: it fills per-phase
   * cells from the BoM rollup and the project intent under the
   * investment-grade rule (real sources only, never clobber a manual edit),
   * and it now carries the commercial-terms snapshot the campus sync used to
   * own. The campus writer is retired rather than left as a second path that
   * silently disagrees.
   */
  const handleSyncCapex = async () => {
    const res = await pullFinancialFromSources(site.id);
    if (res.ok) {
      const filled = res.filledFieldCount;
      toast.success(
        filled > 0
          ? `Financial model updated from BoM + intent: ${filled} field${filled === 1 ? '' : 's'} filled across ${res.phaseCount} phase${res.phaseCount === 1 ? '' : 's'}.`
          : 'Financial model is already up to date with the BoM and project intent.',
      );
      // A4 — a CAPEX LEAK is not a success. Priced BoM lines whose `phaseId`
      // matches no phase (or that sit in a phase with no capacity) drop out of
      // the per-phase rollup, so the financial model is SHORT by that amount
      // while the sync reports a clean fill. Say so, with the number.
      if (res.capexUnreconciledUsd > 0) {
        toast.warning(
          `$${(res.capexUnreconciledUsd / 1_000_000).toFixed(2)}M of priced BoM cost did not reach the `
          + `financial model${res.capexOrphanedLineCount > 0
            ? ` (${res.capexOrphanedLineCount} line${res.capexOrphanedLineCount === 1 ? '' : 's'} reference a phase that no longer exists)`
            : ''}. Re-assign those lines to a phase, or the model is short by that amount.`,
        );
      }
    } else {
      toast.error(res.error);
    }
  };

  const handleSyncSiteWorks = async () => {
    const res = await syncSiteWorksBomFromLayout(site.id);
    if (res.ok) {
      toast.success(
        `Site-works quantities synced from the promoted layout: ${res.added} added, ${res.updated} updated${res.removed > 0 ? `, ${res.removed} removed` : ''}. Price the lines, then sync CAPEX.`,
      );
    } else {
      toast.error(res.error);
    }
  };

  const handleQuoteDecision = async (quote: import('../../../types/supplyChain').Quote, action: 'award' | 'reject') => {
    const res = await awardOrRejectQuote(site.id, quote.id, action);
    if (res.ok && action === 'award') {
      toast.message('BoM CAPEX changed — review financial model sync on Overview.');
    } else if (!res.ok) {
      toast.error(res.error);
    }
  };

  const openAttachmentPicker = (
    target:
      | { type: 'bom'; itemId: string }
      | { type: 'award'; itemId: string }
      | { type: 'quote'; quoteId: string },
  ) => {
    setAttachmentTarget(target);
    setAttachmentDocumentId('');
    setAttachmentError(null);
    if (attachedDocuments.length > 0) setAttachmentPickerOpen(true);
    else attachmentRef.current?.click();
  };

  function readAttachedSource(documentId: string) {
    const matches = useStore.getState().siteRag.sites.filter(candidate => candidate.id === site.id);
    const currentSite = matches.length === 1 ? matches[0] : undefined;
    const currentPlan = currentSite?.supplyChainPlan;
    const documents = currentSite?.documents?.filter(doc => doc.id === documentId) ?? [];
    if (!currentSite || !currentPlan || documents.length !== 1) {
      throw new Error('This document is no longer attached to the site. Choose another document.');
    }
    return { currentSite, currentPlan, document: documents[0]! };
  }

  const handleAttachExistingDocument = async () => {
    if (!attachmentTarget || !attachmentDocumentId || linkingExisting) return;
    setLinkingExisting(true);
    setAttachmentError(null);
    try {
      const { currentPlan, document } = readAttachedSource(attachmentDocumentId);
      const target = attachmentTarget;
      const quote = target.type === 'quote'
        ? currentPlan.quotes.find(candidate => candidate.id === target.quoteId)
        : undefined;
      const itemId = target.type === 'quote' ? quote?.bomItemId : target.itemId;
      if (!itemId || currentPlan.bom.filter(item => item.id === itemId).length !== 1
        || (target.type === 'quote' && currentPlan.quotes.filter(candidate => candidate.id === target.quoteId).length !== 1)) {
        throw new Error('The selected BoM item or quote has changed. Reopen Attach document.');
      }
      await linkBomSourceDocument(site.id, document.id, { type: 'attach', target });
      setAttachmentPickerOpen(false);
      setAttachmentTarget(null);
      toast.success(`Attached ${document.fileName}.`);
    } catch (error) {
      setAttachmentError(error instanceof Error ? error.message : 'Document could not be attached. Try again.');
    } finally {
      setLinkingExisting(false);
    }
  };

  const handleAttachmentFile = async (file: File) => {
    if (!attachmentTarget) return;
    // Surface upload-in-progress on the relevant BoM row (quotes have their
    // own surface). Set BEFORE the upload starts; cleared in `finally` so the
    // indicator clears on both success and error.
    const busyField: BomAttachmentField | null =
      attachmentTarget.type === 'award'
        ? 'awardAttachments'
        : attachmentTarget.type === 'bom'
          ? 'attachments'
          : null;
    if (busyField && attachmentTarget.type !== 'quote') {
      setAttachBusy({ itemId: attachmentTarget.itemId, field: busyField });
    }
    try {
      const doc = await addSiteDocument(site.id, file);
      const now = nowIso();
      const res = await updateSupplyChainPlan(site.id, (current) => {
        if (attachmentTarget.type === 'quote') {
          return {
            ...current,
            quotes: current.quotes.map((q) => (
              q.id === attachmentTarget.quoteId
                ? {
                    ...q,
                    attachments: [
                      ...(q.attachments ?? []),
                      { documentId: doc.id, fileName: doc.fileName, uploadedAt: now },
                    ],
                  }
                : q
            )),
            updatedAt: now,
          };
        }
        const field = attachmentTarget.type === 'award' ? 'awardAttachments' : 'attachments';
        return {
          ...current,
          bom: current.bom.map((item) => (
            item.id === attachmentTarget.itemId
              ? {
                  ...item,
                  [field]: [
                    ...((item[field] as BomItem['attachments']) ?? []),
                    { documentId: doc.id, fileName: doc.fileName, uploadedAt: now },
                  ],
                }
              : item
          )),
          updatedAt: now,
        };
      });
      if (!res.ok) toast.error(`Attach document failed: ${res.error}`);
      else toast.success(`Attached ${doc.fileName}.`);
    } catch (err) {
      toast.error(`Attach document failed: ${err instanceof Error ? err.message : 'upload error'}`);
    } finally {
      setAttachBusy(null);
    }
  };

  const handleRemoveBomDocument = async (
    item: BomItem,
    field: BomAttachmentField,
    documentId: string,
  ) => {
    const res = await updateSupplyChainPlan(site.id, (current) => ({
      ...current,
      bom: current.bom.map((b) => (
        b.id === item.id
          ? {
              ...b,
              [field]: ((b[field] as BomItem['attachments']) ?? []).filter(
                (a) => a.documentId !== documentId,
              ),
            }
          : b
      )),
      updatedAt: nowIso(),
    }));
    if (!res.ok) toast.error(`Unlink document failed: ${res.error}`);
    else toast.success('Document unlinked.');
  };

  const BOM_ACCEPT = '.pdf,.xlsx,.xls,.csv,application/pdf,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv';
  const QUOTE_ACCEPT = '.pdf,.xlsx,.xls,.csv,application/pdf,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv';

  if (!hasUserBom) {
    return (
      <TabShell data-testid="supply-chain-panel">
        {/* aurora-qa-3 — section header removed (William): the Equipment (BOM)
            tab strip already labels this section, so the in-panel
            SiteTabSectionHeaderFromMeta was redundant. BOM content starts clean. */}
        <input
          ref={bomUpload.fileRef}
          type="file"
          accept={BOM_ACCEPT}
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void bomUpload.handleFile(f);
          }}
        />
        <input
          ref={quoteUpload.fileRef}
          type="file"
          accept={QUOTE_ACCEPT}
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void quoteUpload.handleFile(f);
          }}
        />
        <SupplyChainLandingCard
          isUploadingBom={bomUpload.busy}
          isUploadingQuote={quoteUpload.busy}
          onUploadBom={bomUpload.openFilePicker}
          onUploadQuote={quoteUpload.openFilePicker}
          onAddManualItem={() => setManualAddOpen(true)}
          onSyncSiteWorksFromLayout={site.promotedGenerationScenarioSnapshot ? handleSyncSiteWorks : undefined}
        />
        {plan && (
          <MaterialScopeLimitationsEditor
            plan={plan}
            documents={site.documents ?? []}
            onSave={rows => updateSupplyChainPlan(site.id, current => ({ ...current, materialScopeLimitations: rows }))}
          />
        )}
        <BomItemEditDialog
          open={manualAddOpen}
          mode="add"
          suppliers={bomSuppliers}
          companyId={companyId ?? site.companyId}
          phases={bomPhases}
          defaultPhaseId={defaultPhaseId}
          onCreateSupplier={handleCreateSupplierInline}
          onSaveSupplierToCompany={handleSaveSupplierToCompany}
          onCancel={() => setManualAddOpen(false)}
          onSubmit={(payload) => { void handleLandingManualCreate(payload); }}
        />
        <BomUploadReviewDialog
          open={bomUpload.reviewOpen}
          lines={bomUpload.extractedLines}
          warnings={bomUpload.warnings}
          busy={bomUpload.busy}
          mode={bomUpload.mode}
          onModeChange={bomUpload.setMode}
          onConfirm={(lines) => { void bomUpload.confirmApply(lines); }}
          onCancel={bomUpload.cancelReview}
        />
      </TabShell>
    );
  }

  return (
    <TabShell data-testid="supply-chain-panel">
      {/* aurora-qa-3 — section header removed (William): the Equipment (BOM)
          tab strip already labels this section, so the in-panel
          SiteTabSectionHeaderFromMeta was redundant. BOM content starts clean. */}
      <input
        ref={bomUpload.fileRef}
        type="file"
        accept={BOM_ACCEPT}
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void bomUpload.handleFile(f);
        }}
      />
      {/* Quote-upload picker — mounted in the planReady branch too so the
          BoM-row "Add quote" action (handleAddQuoteFromBom ->
          quoteUpload.openFilePicker) has a live input ref to click. Without
          this, quoteUpload.fileRef is null here and the picker is a no-op. */}
      <input
        ref={quoteUpload.fileRef}
        type="file"
        accept={QUOTE_ACCEPT}
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void quoteUpload.handleFile(f);
          e.target.value = '';
        }}
      />
      <input
        ref={attachmentRef}
        type="file"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void handleAttachmentFile(f);
          e.target.value = '';
          setAttachmentTarget(null);
        }}
      />
      <BomUploadReviewDialog
        open={bomUpload.reviewOpen}
        lines={bomUpload.extractedLines}
        warnings={bomUpload.warnings}
        busy={bomUpload.busy}
        mode={bomUpload.mode}
        onModeChange={bomUpload.setMode}
        onConfirm={(lines) => { void bomUpload.confirmApply(lines); }}
        onCancel={bomUpload.cancelReview}
      />
      <ResponsiveTabStrip
        ariaLabel="Supply chain sub-sections"
        tabs={SUB_TABS.map(t => ({
          id: t.id,
          label: t.label,
          shortLabel: t.shortLabel,
          icon: t.icon,
          testId: `scm-tab-${t.id}`,
          tabDomId: `scm-tab-${t.id}`,
          controlsId: `scm-tab-panel-${t.id}`,
        }))}
        activeId={activeTab}
        onSelect={id => setActiveTab(id as SubTab)}
      />

      {!planReady && (
        <p role="status" className="text-xs text-text-muted" data-testid="scm-bom-draft-readiness">
          You can review and edit this draft. Supply Chain Risk requires at least{' '}
          {SUPPLY_CHAIN_MIN_REPORTABLE_BOM_ITEMS} valid manual or uploaded items,
          including one marked critical or critical-path.
        </p>
      )}

      {activeTab !== 'bom' && plan && materialScopeDisclosure(plan) && (
        <p className="whitespace-pre-wrap break-words text-xs text-text-secondary" data-testid="scm-material-scope-summary">
          {materialScopeDisclosure(plan)}
        </p>
      )}

      {activeTab === 'bom' && plan && (
        <MaterialScopeLimitationsEditor
          plan={plan}
          documents={site.documents ?? []}
          onSave={rows => updateSupplyChainPlan(site.id, current => ({ ...current, materialScopeLimitations: rows }))}
        />
      )}

      <div
        className="min-h-[12rem]"
        role="tabpanel"
        id={`scm-tab-panel-${activeTab}`}
        aria-labelledby={`scm-tab-${activeTab}`}
      >
        {activeTab === 'overview' && (
          <SupplyChainOverviewPanel
            plan={plan}
            inServiceDate={inServiceDate}
            site={site}
            onSyncCapexToFinancialModel={handleSyncCapex}
          />
        )}
        {activeTab === 'bom' && (
          <SupplyChainBomPanel
            plan={plan}
            suppliers={bomSuppliers}
            companyId={companyId ?? site.companyId}
            onSyncSiteWorksFromLayout={site.promotedGenerationScenarioSnapshot ? handleSyncSiteWorks : undefined}
            onJumpToSuppliersWithFilter={jumpToSuppliersWithFilter}
            onAddQuote={handleAddQuoteFromBom}
            onEmailSupplier={(item, supplierId) => {
              const supplier = bomSuppliers.find(s => s.id === supplierId);
              if (supplier) openSupplierEmail(supplier, item);
            }}
            onAttachBomDocument={(item) => openAttachmentPicker({ type: 'bom', itemId: item.id })}
            onAttachAwardDocument={(item) => openAttachmentPicker({ type: 'award', itemId: item.id })}
            attachBusy={attachBusy}
            onOpenDocument={setViewerDocId}
            onRemoveBomDocument={(item, field, documentId) => {
              void handleRemoveBomDocument(item, field, documentId);
            }}
            onCreateItem={handleCreateBomItem}
            onCreateSupplier={handleCreateSupplierInline}
            onUpdateItem={handleUpdateBomItem}
            onDeleteItem={handleDeleteBomItem}
            onImportBom={() => {
              bomUpload.setMode('merge');
              bomUpload.openFilePicker();
            }}
            onStatusTransition={(item, toStatus, opts) => {
              void updateBomItemStatus(site.id, item.id, toStatus, opts);
            }}
            onGenerateSimulation={simulationComputeEnabled && bomSimulationGenerationEnabled
              ? () => setGenerateSimulationOpen(true)
              : undefined}
          />
        )}
        {activeTab === 'suppliers' && (
          <SupplyChainSuppliersPanel
            plan={plan}
            companyId={companyId ?? site.companyId}
            reactorVendorId={site.projectIntent?.reactorVendorId}
            initialFilter={suppliersFilter}
            initialBomItemId={suppliersBomItemId}
            onAddSupplier={handleAddSupplier}
            onAddFromDirectory={async (supplierId) => {
              const res = await addSupplierFromDirectory({
                siteId: site.id,
                supplierId,
                companyId: companyId ?? site.companyId,
              });
              if (res.ok) toast.success('Supplier added to project.');
              else toast.error(res.error);
            }}
            onAttachSupplierToBomItem={handleAttachSupplierToBomItem}
            onEmailSupplier={(supplier) => { handleEmailSupplier(supplier); }}
          />
        )}
        {activeTab === 'quotes' && (
          <SupplyChainQuotesPanel
            plan={plan}
            documents={site.documents ?? []}
            onDecision={(quote, action) => { void handleQuoteDecision(quote, action); }}
            onUploadQuoteFile={handleUploadQuoteFile}
            onAddManualQuote={handleAddManualQuote}
            onAttachQuoteDocument={(quote: Quote) => openAttachmentPicker({ type: 'quote', quoteId: quote.id })}
          />
        )}
        {activeTab === 'timeline' && (
          <SupplyChainTimelinePanel plan={plan} inServiceDate={inServiceDate} />
        )}
      </div>
      <Dialog open={attachmentPickerOpen} onOpenChange={open => {
        if (linkingExisting) return;
        setAttachmentPickerOpen(open);
        if (!open) setAttachmentTarget(null);
      }}>
          <DialogContent size="sm" showCloseButton={!linkingExisting} data-testid="scm-existing-document-dialog">
            <DialogTitle className="text-sm font-semibold">Attach document</DialogTitle>
            <label className="block space-y-1 type-body text-text-secondary">
              <span>Site document</span>
              <select
                value={attachmentDocumentId}
                onChange={e => setAttachmentDocumentId(e.target.value)}
                className="w-full px-2 py-1.5 bg-background border border-border text-xs text-foreground"
                data-testid="scm-existing-document-select"
              >
                <option value="">Select a document</option>
                {attachedDocuments.map(doc => <option key={doc.id} value={doc.id}>{doc.fileName}</option>)}
              </select>
            </label>
            {attachmentError && <p role="alert" className="text-xs text-danger">{attachmentError}</p>}
            <div className="flex flex-wrap items-center gap-2">
              <button type="button" disabled={!attachmentDocumentId || linkingExisting} onClick={() => { void handleAttachExistingDocument(); }} className="px-3 py-1.5 border border-primary/40 text-primary text-xs disabled:opacity-50" data-testid="scm-existing-document-attach">
                {linkingExisting ? 'Attaching…' : 'Attach selected'}
              </button>
              <button type="button" disabled={linkingExisting} onClick={() => { setAttachmentPickerOpen(false); attachmentRef.current?.click(); }} className="px-3 py-1.5 border border-border text-foreground text-xs" data-testid="scm-existing-document-upload">
                Upload new
              </button>
              <button type="button" disabled={linkingExisting} onClick={() => { setAttachmentPickerOpen(false); setAttachmentTarget(null); }} className="px-3 py-1.5 text-text-secondary text-xs">
                Cancel
              </button>
            </div>
          </DialogContent>
      </Dialog>
      {outreachTarget && (
        <SupplierOutreachDialog
          supplier={outreachTarget.supplier}
          bomItem={outreachTarget.bomItem}
          project={supplierOutreachProjectFromSite(site)}
          onClose={() => setOutreachTarget(null)}
        />
      )}
      {simulationComputeEnabled && bomSimulationGenerationEnabled && generateSimulationOpen && (
        <GenerateSimulationWizard
          open={generateSimulationOpen}
          site={site}
          simSite={{ id: site.id, name: site.name, location: site.location, zoom: 14, address: site.address }}
          onClose={() => setGenerateSimulationOpen(false)}
          onGenerated={() => {
            setGenerateSimulationOpen(false);
            // Owner request A — the Canvas opened from this Site (including an
            // unlinked Canvas the result was added to) is linked to this Site.
            const launch = openActiveCanvasFromSite({
              id: site.id,
              name: site.name,
              location: site.location,
              zoom: 14,
              address: site.address,
            });
            const notice = siteLaunchLinkNotice(launch.decision, site.name);
            if (notice) toast.info(notice.message);
            setViewMode('simulation');
            navigate('/editor');
          }}
        />
      )}
      <DocViewerDialog
        doc={viewerDoc}
        open={!!viewerDoc}
        onClose={() => setViewerDocId(null)}
      />
    </TabShell>
  );
};
